import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Grid,
  Button,
  useTheme,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Componente estilizado para o acordeão
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  border: 'none',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '10px',
  marginBottom: '8px',
  overflow: 'hidden',
}));

// Componente estilizado para o sumário do acordeão
const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1, 0),
  },
  '&.Mui-expanded': {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
  },
}));

// Interface para as perguntas frequentes
interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

// Componente para o FAQ expandido
const ExpandedFAQ: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Lista de categorias
  const categories = [
    { id: 'all', name: 'Todas', icon: <QuestionIcon /> },
    { id: 'payment', name: 'Pagamento', icon: <PaymentIcon /> },
    { id: 'trial', name: 'Período de Teste', icon: <ScheduleIcon /> },
    { id: 'security', name: 'Segurança', icon: <SecurityIcon /> },
    { id: 'features', name: 'Funcionalidades', icon: <SettingsIcon /> },
    { id: 'whatsapp', name: 'WhatsApp IA Bot', icon: <WhatsAppIcon /> },
    { id: 'email', name: 'E-mail Response', icon: <EmailIcon /> },
    { id: 'social', name: 'Redes Sociais', icon: <ShareIcon /> },
  ];

  // Lista de perguntas frequentes
  const faqItems: FAQItem[] = [
    // Pagamento
    {
      question: 'Quais formas de pagamento são aceitas?',
      answer: 'Aceitamos cartão de crédito (Visa, Mastercard, American Express, Elo), PIX e boleto bancário. Para planos anuais, oferecemos a opção de parcelamento em até 12x no cartão de crédito.',
      category: 'payment'
    },
    {
      question: 'Posso mudar de plano depois de assinar?',
      answer: 'Sim, você pode fazer upgrade ou downgrade do seu plano a qualquer momento. Se fizer upgrade, você pagará apenas a diferença proporcional ao tempo restante da sua assinatura atual. Se fizer downgrade, o valor proporcional será creditado na sua próxima fatura.',
      category: 'payment'
    },
    {
      question: 'Qual a diferença entre pagamento mensal e anual?',
      answer: 'No pagamento anual, você recebe 20% de desconto em relação ao valor mensal, o que representa uma economia de mais de 2 meses por ano. Além disso, planos anuais têm prioridade no suporte técnico.',
      category: 'payment'
    },
    {
      question: 'Posso cancelar minha assinatura a qualquer momento?',
      answer: 'Sim, você pode cancelar sua assinatura a qualquer momento sem multa ou taxas adicionais. Se cancelar um plano anual, você receberá um reembolso proporcional ao tempo não utilizado, descontando o valor do desconto concedido.',
      category: 'payment'
    },
    
    // Período de Teste
    {
      question: 'Como funciona o período de teste gratuito?',
      answer: 'Oferecemos 15 dias de acesso completo a todas as funcionalidades do sistema, sem restrições. Durante este período, você pode explorar todos os recursos do plano Premium sem compromisso de assinatura.',
      category: 'trial'
    },
    {
      question: 'Preciso fornecer dados de pagamento para iniciar o período de teste?',
      answer: 'Não, você não precisa fornecer dados de pagamento para iniciar o período de teste. Solicitamos apenas seu e-mail e informações básicas para criar sua conta.',
      category: 'trial'
    },
    {
      question: 'O que acontece quando o período de teste termina?',
      answer: 'Ao final dos 15 dias, se você não assinar um plano pago, sua conta será automaticamente convertida para o plano Free, que possui limitações de funcionalidades. Você receberá notificações alguns dias antes do término do período de teste.',
      category: 'trial'
    },
    {
      question: 'Posso estender meu período de teste?',
      answer: 'Em casos especiais, podemos estender o período de teste por mais alguns dias. Entre em contato com nosso suporte para verificar esta possibilidade.',
      category: 'trial'
    },
    
    // Segurança
    {
      question: 'Como meus dados são protegidos?',
      answer: 'Utilizamos criptografia de ponta a ponta para proteger seus dados. Todas as informações são armazenadas em servidores seguros com backup diário. Estamos em conformidade com a LGPD (Lei Geral de Proteção de Dados) e implementamos medidas de segurança rigorosas para proteger suas informações.',
      category: 'security'
    },
    {
      question: 'Quem tem acesso aos meus dados?',
      answer: 'Apenas você e os usuários autorizados da sua conta têm acesso aos seus dados. Nossa equipe técnica pode acessar informações limitadas apenas para fins de suporte técnico, sempre com seu consentimento prévio.',
      category: 'security'
    },
    {
      question: 'O que acontece com meus dados se eu cancelar minha assinatura?',
      answer: 'Se você cancelar sua assinatura, seus dados permanecerão armazenados por 30 dias, permitindo que você os exporte se necessário. Após este período, todos os dados serão permanentemente excluídos de nossos servidores.',
      category: 'security'
    },
    
    // Funcionalidades
    {
      question: 'Quais são as principais diferenças entre os planos?',
      answer: 'A principal diferença está nos limites de uso e acesso a recursos avançados. O plano Free tem limites básicos (50 leads, 20 produtos), o Intermediário oferece limites maiores (200 leads, 50 produtos) e recursos adicionais, enquanto o Premium oferece uso ilimitado e acesso a todas as funcionalidades avançadas, como múltiplos usuários e base de conhecimento ilimitada.',
      category: 'features'
    },
    {
      question: 'Posso personalizar as funcionalidades do meu plano?',
      answer: 'Para empresas com necessidades específicas, oferecemos planos personalizados. Entre em contato com nossa equipe comercial para discutir suas necessidades e obter uma proposta customizada.',
      category: 'features'
    },
    {
      question: 'Como faço para adicionar mais usuários à minha conta?',
      answer: 'No plano Intermediário, você pode ter até 3 usuários. No plano Premium, até 10 usuários. Para adicionar mais usuários além desses limites, oferecemos pacotes adicionais de usuários que podem ser contratados separadamente.',
      category: 'features'
    },
    
    // WhatsApp IA Bot
    {
      question: 'Como funciona o WhatsApp IA Bot?',
      answer: 'O WhatsApp IA Bot utiliza inteligência artificial avançada para responder automaticamente às mensagens dos seus clientes. Ele pode identificar intenções, responder perguntas sobre produtos, capturar leads e muito mais. A tecnologia RAG (Retrieval-Augmented Generation) permite que ele utilize sua base de conhecimento para fornecer respostas contextualizadas.',
      category: 'whatsapp'
    },
    {
      question: 'Preciso de um número de WhatsApp dedicado para o bot?',
      answer: 'Sim, recomendamos utilizar um número dedicado para o bot. Você pode usar um número existente ou adquirir um novo. O importante é que este número seja utilizado exclusivamente para o bot, para evitar conflitos com o uso pessoal.',
      category: 'whatsapp'
    },
    {
      question: 'Como o bot captura leads automaticamente?',
      answer: 'Quando um novo contato envia uma mensagem, o bot pode extrair automaticamente informações como nome e número de telefone, e cadastrá-lo como lead no sistema. Você pode configurar campos adicionais para serem capturados durante a conversa.',
      category: 'whatsapp'
    },
    {
      question: 'Posso personalizar as respostas do bot?',
      answer: 'Sim, você pode personalizar completamente as respostas do bot, criar fluxos de conversa específicos e adicionar documentos à base de conhecimento para que o bot possa fornecer informações precisas sobre seus produtos e serviços.',
      category: 'whatsapp'
    },
    
    // E-mail Response
    {
      question: 'Como funciona o assistente de e-mail com IA?',
      answer: 'O assistente de e-mail com IA analisa os e-mails recebidos, classifica-os por categoria/intenção e gera respostas automáticas contextualizadas. Ele utiliza sua base de conhecimento para personalizar as respostas de acordo com seu negócio.',
      category: 'email'
    },
    {
      question: 'Posso integrar com meu provedor de e-mail atual?',
      answer: 'Sim, o sistema é compatível com a maioria dos provedores de e-mail, incluindo Gmail, Outlook, Yahoo e servidores IMAP/SMTP personalizados. A configuração é simples e oferecemos suporte para ajudar na integração.',
      category: 'email'
    },
    {
      question: 'Como funciona a automação de e-mails?',
      answer: 'Você pode criar regras para envio automático de e-mails com base em gatilhos específicos, como resposta a determinados tipos de mensagens, follow-up após um período de tempo, ou envio programado de campanhas. Todas as automações podem ser personalizadas e testadas antes de serem ativadas.',
      category: 'email'
    },
    
    // Redes Sociais
    {
      question: 'Quais plataformas de redes sociais são suportadas?',
      answer: 'Suportamos Instagram, Facebook, YouTube, TikTok, X (antigo Twitter) e Google Meu Negócio. No plano Free, você tem acesso a 2 plataformas, no Intermediário a 4 plataformas, e no Premium a todas as plataformas disponíveis.',
      category: 'social'
    },
    {
      question: 'Como funciona o assistente de IA para redes sociais?',
      answer: 'O assistente de IA para redes sociais pode gerar conteúdo otimizado para cada plataforma, sugerir hashtags relevantes, analisar tendências do setor e oferecer ideias para posts e campanhas. Ele também pode otimizar textos para maior engajamento.',
      category: 'social'
    },
    {
      question: 'É possível agendar posts para várias plataformas ao mesmo tempo?',
      answer: 'Sim, você pode criar um post e agendá-lo para ser publicado em múltiplas plataformas simultaneamente. O sistema adaptará automaticamente o formato para cada plataforma, respeitando suas características específicas.',
      category: 'social'
    },
  ];

  // Filtrar perguntas com base na categoria selecionada e no termo de busca
  const filteredFAQs = faqItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: '16px', 
        mb: 4,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative',
        p: 3
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '5px', 
        background: 'linear-gradient(90deg, #3f51b5, #f50057)'
      }} />
      
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Perguntas Frequentes
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Buscar perguntas frequentes..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { 
              borderRadius: '12px',
              bgcolor: 'white',
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.1)',
              },
            }
          }}
          sx={{ mb: 2 }}
        />
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {categories.map(category => (
            <Chip 
              key={category.id}
              icon={category.icon}
              label={category.name}
              onClick={() => setSelectedCategory(category.id)}
              color={selectedCategory === category.id ? "primary" : "default"}
              variant={selectedCategory === category.id ? "filled" : "outlined"}
              sx={{ fontWeight: selectedCategory === category.id ? 'bold' : 'normal' }}
            />
          ))}
        </Box>
      </Box>
      
      {filteredFAQs.length > 0 ? (
        <Box>
          {filteredFAQs.map((faq, index) => (
            <StyledAccordion key={index}>
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <Typography fontWeight="medium">{faq.question}</Typography>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhuma pergunta encontrada para sua busca.
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
            }}
          >
            Limpar filtros
          </Button>
        </Box>
      )}
      
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          Não encontrou o que procurava?
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          sx={{ 
            mt: 1,
            fontWeight: 'medium',
            textTransform: 'none',
            borderRadius: '8px',
            px: 3
          }}
          startIcon={<WhatsAppIcon />}
        >
          Falar com Suporte
        </Button>
      </Box>
    </Paper>
  );
};

export default ExpandedFAQ;
